import { ImportConfigurationEditFormsCoordinatorService } from '../../../../import-configuration-edit-forms-coordinator.service';
import { ImportFunctionDefinitionDTO, ImportFunction, ImportFieldAssignmentTargetDefinitionDTO, ImportSourceFieldDefinitionDTO } from './../../../../../../../../base/models/models.swagger.generated';
import { DropDownConstantsFactory } from './../../../../../../../../base/shared/syncfusion/dropdown-constants-factory';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FieldAssignmentGridEditFormType } from './../field-assignment-grid-edit-form-type';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { YesNoGroupButtonComponent } from '../../../../../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { NgClass } from '@angular/common';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AppErrorMessagesComponent } from '../../../../../../../../base/error-messages/app-error-messages.component';
import { ImportConfigurationFileFormType } from '../../../configuration-form-file-form-type';
import { FieldAssignmentDefinitionsService } from 'src/app/domain/import-configuration/shared/field-assignment-definitions.service';

class ExtendedImportSourceFieldDefinitionDTO extends ImportSourceFieldDefinitionDTO {
  public id: number;
}

@Component({
    selector: 'app-field-assignment-grid-edit-form',
    templateUrl: './field-assignment-grid-edit-form.component.html',
    styleUrls: ['./field-assignment-grid-edit-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, AppErrorMessagesComponent, FormsModule, DropDownListModule, YesNoGroupButtonComponent, NgClass, TranslateModule]
})
export class FieldAssignmentGridEditFormComponent implements OnInit {

  public readonly fieldAssignmentEditForm = input.required<FormGroup<FieldAssignmentGridEditFormType>>();
  public readonly isColumnBased = input.required<boolean>();

  public readonly formsCoordinationService = inject(ImportConfigurationEditFormsCoordinatorService);
  private readonly fieldAssignmentDefinitionsService = inject(FieldAssignmentDefinitionsService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly enumDropdownSettings = DropDownConstantsFactory.createEnumDropdownSettings();
  public readonly availableSourcesDropdownSettings = {
    text: 'sourceElementName',
    value: 'id'
  };
  public readonly ImportFunction = ImportFunction;

  public readonly targetDefinition = signal<ImportFieldAssignmentTargetDefinitionDTO | undefined>(undefined);
  public readonly functionDefinition = signal<ImportFunctionDefinitionDTO | undefined>(undefined);
  public readonly possibleSources = signal<ExtendedImportSourceFieldDefinitionDTO[]>([]);
  public readonly useImportColumn = signal(true);
  public readonly selectedSourceFieldId = signal<number | null>(null);

  private readonly importFunctionValue = signal<ImportFunction | null>(null);
  public readonly isStaticFunctionUsed = computed(() => {
    const value = this.importFunctionValue();
    return value === ImportFunction.ToToday
      || value === ImportFunction.FixDate
      || value === ImportFunction.NoData
      || value === ImportFunction.VendorNoLookup;
  });

  public get configurationFormFile(): FormGroup<ImportConfigurationFileFormType> {
    return this.formsCoordinationService.configurationFormFile;
  }

  private get form(): FormGroup<FieldAssignmentGridEditFormType> {
    return this.fieldAssignmentEditForm();
  }

  public getColumnDisplayName(data: ImportSourceFieldDefinitionDTO): string {
    if (!data) {
      return '';
    }
    return data.sampleValue ? `${data.sourceElementName} (${data.sampleValue})` : data.sourceElementName;
  }

  public onSourceFieldSelected(id: number | null): void {
    // source field name might not be unique, so we need to use the id to identify the source field
    if (this.selectedSourceFieldId() === id) {
      return;
    }
    this.selectedSourceFieldId.set(id);
    const source = id != null ? this.possibleSources().find(s => s.id === id) : undefined;
    this.form.controls.sourceElementName.setValue(source?.sourceElementName ?? null);
    this.form.controls.sourceElementSelectionPath.setValue(source?.sourceElementSelectionPath ?? null);
    // Mirror the dirty-marking behavior of a native input's "input" event, since this control's
    // value is only ever set programmatically through this dropdown instead of formControlName.
    this.form.controls.sourceElementName.markAsDirty();
  }

  // Unconditionally clears the import-column selection, regardless of isColumnBased (dropdown vs.
  // plain text input) and regardless of the current selectedSourceFieldId - unlike
  // onSourceFieldSelected, this must not no-op just because no dropdown id was ever tracked.
  private clearSelectedSourceField(): void {
    this.selectedSourceFieldId.set(null);
    this.form.controls.sourceElementName.setValue(null);
    this.form.controls.sourceElementSelectionPath.setValue(null);
  }

  public onSourceFieldDropdownClosed(): void {
    // Mirror a native input's blur behavior so validation feedback becomes visible once the user
    // has interacted with the dropdown, consistent with formControlName-bound inputs.
    this.form.controls.sourceElementName.markAsTouched();
  }

  public setDefaultFixValue(): void {
    if (this.useImportColumn()) {
      return;
    }
    const fixValueControl = this.form.controls.fixValue;
    if (!fixValueControl.value) {
      fixValueControl.setValue(this.targetDefinition()?.defaultFixValue ?? null);
    }
  }

  // Single deterministic handler for the use-import-column / use-fix-value toggle: guarantees
  // validators are updated before the default fix value is applied, instead of relying on the
  // relative order of two independent event listeners on the same native event.
  public onUseImportColumnChange(value: boolean): void {
    this.useImportColumn.set(value);
    // The mode is re-derived from which of these two values is present the next time the dialog
    // opens (see determineInitialUseImportColumn), so the value belonging to the mode being left
    // must be cleared - otherwise both values are saved and the wrong mode is inferred on reopen.
    if (value) {
      this.form.controls.fixValue.setValue(null);
    } else {
      this.clearSelectedSourceField();
    }
    this.applyFieldRequirements();
    if (!value) {
      this.setDefaultFixValue();
    }
  }

  public ngOnInit(): void {
    this.possibleSources.set(this.configurationFormFile.controls.possibleSources.value.map((source, index) => ({
      ...source,
      id: index
    } as ExtendedImportSourceFieldDefinitionDTO)));

    // source field name might not be unique, so we need to use the id to identify the source field
    const selectedSource = this.possibleSources().find(s =>
      s.sourceElementName === this.form.controls.sourceElementName.value
      && s.sourceElementSelectionPath === this.form.controls.sourceElementSelectionPath.value);
    if (selectedSource) {
      this.selectedSourceFieldId.set(selectedSource.id);
    }

    this.importFunctionValue.set(this.form.controls.importFunction.value);
    this.updateTargetDefinition();
    this.updateFunctionDefinition();

    // Derive the initial toggle state from the fully resolved form data (after target/function
    // definitions and any auto-preselection have been applied), and apply validators exactly once
    // with the final, deterministic values.
    this.useImportColumn.set(this.determineInitialUseImportColumn());
    this.applyFieldRequirements();

    this.form.controls.importFunction.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.importFunctionValue.set(value);
        this.updateFunctionDefinition();
      });
  }

  private determineInitialUseImportColumn(): boolean {
    if (!this.targetDefinition()?.canHaveFixValue) {
      return true;
    }
    const hasFixValue = !!this.form.controls.fixValue.value;
    const hasSourceElementName = !!this.form.controls.sourceElementName.value;
    return !(hasFixValue && !hasSourceElementName);
  }

  private updateTargetDefinition(): void {
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const targetDefinitions = this.fieldAssignmentDefinitionsService.getTargetDefinitionsImmediately(importType);
    const targetDefinition = targetDefinitions.find(td => td.targetName === this.form.controls.targetName.value);
    this.targetDefinition.set(targetDefinition);

    //preselect function if only 1
    if (targetDefinition.supportedFunctions.length === 1) {
      this.form.controls.importFunction.setValue(targetDefinition.supportedFunctions[0]);
      this.importFunctionValue.set(targetDefinition.supportedFunctions[0]);
    }
  }

  private updateFunctionDefinition(): void {
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const functionDefinitions = this.fieldAssignmentDefinitionsService.getFunctionDefinitionsImmediately(importType);
    this.functionDefinition.set(functionDefinitions.find(fd => fd.importFunction === this.form.controls.importFunction.value));

    //preselect import row if only 1
    const possibleSourcesValue = this.configurationFormFile.controls.possibleSources?.value;
    if (possibleSourcesValue?.length === 1) {
      if (this.isColumnBased()) {
        // route through onSourceFieldSelected so the dropdown's displayed selection never diverges
        // from the underlying sourceElementName control value
        this.onSourceFieldSelected(this.possibleSources()[0].id);
      } else {
        this.form.controls.sourceElementName.setValue(possibleSourcesValue[0].sourceElementName);
      }
    }

    this.updateAllFunctionDefinitionRequired();
  }

  private updateAllFunctionDefinitionRequired(): void {
    const functionDefinition = this.functionDefinition();
    this.setParameterValidator(this.form.controls.functionParameter1Value, functionDefinition?.parameter1?.isMandatory);
    this.setParameterValidator(this.form.controls.functionParameter2Value, functionDefinition?.parameter2?.isMandatory);
    this.setParameterValidator(this.form.controls.functionParameter3Value, functionDefinition?.parameter3?.isMandatory);
    this.setParameterValidator(this.form.controls.functionParameter4Value, functionDefinition?.parameter4?.isMandatory);
    this.setParameterValidator(this.form.controls.functionParameter5Value, functionDefinition?.parameter5?.isMandatory);
    this.applyFieldRequirements();
  }

  private setParameterValidator(control: FormControl<string>, isMandatory: boolean | undefined): void {
    control.setValidators(isMandatory ? [Validators.required] : []);
    control.updateValueAndValidity();
  }

  // Single source of truth for the validators of sourceElementName and fixValue. Always derives
  // its outcome purely from current state (isStaticFunctionUsed, useImportColumn) so it produces
  // the same result regardless of which code path triggered it.
  private applyFieldRequirements(): void {
    const sourceElementNameControl = this.form.controls.sourceElementName;
    const fixValueControl = this.form.controls.fixValue;

    if (this.isStaticFunctionUsed()) {
      // A function which leads to a static value (e.g. ToToday) needs neither an import column nor a fix value.
      sourceElementNameControl.setValidators([]);
      fixValueControl.setValidators([]);
      sourceElementNameControl.setValue(null);
      fixValueControl.setValue(null);
    } else if (this.useImportColumn()) {
      sourceElementNameControl.setValidators([Validators.required]);
      fixValueControl.setValidators([]);
    } else {
      sourceElementNameControl.setValidators([]);
      fixValueControl.setValidators([Validators.required]);
    }

    sourceElementNameControl.updateValueAndValidity();
    fixValueControl.updateValueAndValidity();
  }
}
